import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { FeatureClasses, NauticalClasses } from "../../constants/NauticalObjects";
import { GetCodedValueAndAlias } from "../../utils/arcgisUtils";
import FeatureField from "./FeatureField";
import { getValidSeamarkRange } from "../../utils/autofillUtils";
import { useEffect, useState } from "react";
import {
    LayerFieldsCommon,
    LayerFieldsFastSjomerke,
    LayerFieldsFlytende,
    LayerFieldsLys,
} from "../../constants/LayerFields";
import { Sjomerketype } from "../../constants/nfsDomains";
import { AppConfig } from "../../AppConfig";

interface FeatureFieldWithLabelProps {
    feature: Graphic;
    isEditing: boolean;
    attributeKey: string;
    hideNonEditable?: boolean;
    canBeSentToFdv: boolean;
    secondaryFeature?: Graphic;
    setHasEditedAttributes: (hasEditedAttributes: boolean) => void;
}

const FeatureFieldWithLabel = (props: FeatureFieldWithLabelProps) => {
    const { attributeKey, feature, isEditing, hideNonEditable } = props;

    const fieldname = GetCodedValueAndAlias(feature.layer, attributeKey, feature.attributes[attributeKey])[0];
    const [rangeMin, setRangeMin] = useState<number | string | undefined>(undefined);
    const [rangeMax, setRangeMax] = useState<number | string | undefined>(undefined);
    const [infoMessage, setInfoMessage] = useState<string>("");

    useEffect(() => {
        const fieldInfoLocal = (feature.layer as FeatureLayer)?.fields.find((field) => field.alias === fieldname);
        let rangeMin = undefined;
        let rangeMax = undefined;
        let infoMessage = "";
        if (fieldInfoLocal?.domain?.type === "range") {
            rangeMin = fieldInfoLocal.domain?.minValue;
            rangeMax = fieldInfoLocal.domain?.maxValue;
            // parse to double to avoid issues with string comparison in InputField
            if (typeof rangeMin === "string") {
                rangeMin = Number.parseFloat(rangeMin);
            }
            if (typeof rangeMax === "string") {
                rangeMax = Number.parseFloat(rangeMax);
            }
        }

        if (fieldname === "Sjømerkenummer") {
            // Special case for seamark number, show range if available
            const { lowValue, highValue, message } = getValidSeamarkRange(feature);
            rangeMin = lowValue;
            rangeMax = highValue;
            infoMessage = message;
        }
        setRangeMin(rangeMin);
        setRangeMax(rangeMax);
        setInfoMessage(infoMessage);
    }, [feature.attributes]);

    if (feature.layer?.hasOwnProperty("fields") === false) {
        return null;
    }
    const fieldInfo = (feature.layer as FeatureLayer)?.fields.find((field) => field.alias === fieldname);

    const attributes = feature.attributes as { [key: string]: any };

    const shouldRender = (): boolean => {
        if (feature.layer == null) {
            return true;
        }

        if (
            (feature.layer as FeatureLayer).layerId === FeatureClasses.Fastsjømerke &&
            (attributeKey === LayerFieldsFastSjomerke.farge_1 ||
                attributeKey === LayerFieldsFastSjomerke.farge_2 ||
                attributeKey === LayerFieldsFastSjomerke.farge_3)
        ) {
            return false;
        }

        if (
            (feature.layer as FeatureLayer).layerId === FeatureClasses.Fastsjømerke &&
            attributeKey === LayerFieldsFastSjomerke.radar_reflektor &&
            attributes.klasse === NauticalClasses.SG_Stang
        ) {
            return false;
        }

        switch (attributeKey) {
            case LayerFieldsFlytende.fargemonster: {
                if (
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.BabordLateralmerke ||
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.StyrbordLateralmerke
                ) {
                    return false;
                }
                return true;
            }
            case LayerFieldsFlytende.farge_2: {
                if (
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.BabordLateralmerke ||
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.StyrbordLateralmerke ||
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.Spesialmerke
                ) {
                    return false;
                }
                return true;
            }
            case LayerFieldsFlytende.farge_3: {
                if (
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.BabordLateralmerke ||
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.StyrbordLateralmerke ||
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.Nordkardinalmerke ||
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.Sorkardinalmerke ||
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.Senterled ||
                    attributes[LayerFieldsFlytende.sjomerke_type] === Sjomerketype.Spesialmerke
                ) {
                    return false;
                }
                return true;
            }
            case LayerFieldsLys.linse_type:
            case LayerFieldsLys.antall_identiske_lys:
            case LayerFieldsLys.nav_id:
            case LayerFieldsLys.lys:
                return false;
            case LayerFieldsLys.linse_orden:
                return attributes.klasse === NauticalClasses.FL_Fyrlykt;
            case LayerFieldsCommon.byggehoyde:
                return (
                    attributes.klasse !== NauticalClasses.LB_Lysboeye && attributes.klasse !== NauticalClasses.ST_Stake
                );
            case LayerFieldsLys.retningslys_retning:
                return [
                    NauticalClasses.HIB_HurtigbaatmerkeMedIndirekteBelysning,
                    NauticalClasses.IB_IndirekteBelysning,
                ].includes(attributes.klasse);
            case LayerFieldsCommon.beskrivelse: {
                if ((feature.layer as FeatureLayer).layerId === FeatureClasses.Lys) {
                    return attributes.klasse !== NauticalClasses.LB_Lysboeye;
                }
                return true;
            }
            default:
                return true;
        }
    };

    if (
        (hideNonEditable && (AppConfig.FeatureEditor.ReadonlyFields.includes(attributeKey) || !fieldInfo?.editable)) ||
        !feature.layer
    ) {
        if (attributeKey === "Lat" || attributeKey === "Long") {
            // If Lat/Long is not editable, we can still show it
        } else {
            return null;
        }
    }

    const renderHistoricValue = () => {
        if (feature.attributes[`${props.attributeKey}_h`]) {
            return (
                <span
                    className="feature-viewer-historic-value-label"
                    title={`${feature.attributes[`${props.attributeKey}_h`]}`}
                >
                    Se verdi fra gammel NFS
                </span>
            );
        } else return null;
    };

    if (!shouldRender()) {
        return null;
    }

    return (
        <div className="feature-viewer-feature-attribute">
            <div className="feature-viewer-feature-attribute-label">
                <span className="feature-viewer-key text-left">
                    {fieldname}
                    {rangeMin !== undefined && rangeMax !== undefined ? ` (${rangeMin} - ${rangeMax})` : ""}
                </span>
                {renderHistoricValue()}
            </div>

            {infoMessage !== "" && <span className="feature-viewer-info text-left">{infoMessage}</span>}

            <FeatureField
                field={attributeKey}
                layer={feature.layer as FeatureLayer}
                attributes={feature.attributes}
                isEditing={isEditing}
                onChange={() => props.setHasEditedAttributes(true)}
                feature={feature}
                canBeSentToFdv={props.canBeSentToFdv}
                secondaryFeature={props.secondaryFeature}
            />
        </div>
    );
};

export default FeatureFieldWithLabel;
